/**
 * Polling day: SOS (big red button), "my SOS" status from the war room, and turnout reports.
 *
 * SOS: one tap queues the alert with a random clientId and sends it at once, GPS attached if the
 * phone already has a fix (it never waits for GPS). Without network it stays queued and is retried
 * with the same id (the war room never gets it twice); an "SMS से भेजें" button opens the SMS app
 * with the alert written out, and says plainly that the worker must still press Send there.
 *
 * My SOS: GET /sos/mine, fetched when this screen opens, every 30 s while it is visible, when the
 * network comes back and right after an SOS went through; the last list is kept for offline.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { SOS_CATEGORIES, TURNOUT_SLOTS, turnoutPct, type SosCategory, type TurnoutSlot } from "@npac/rules";
import { findBoothByCode, normalizeDigits } from "@core/boothCode";
import { betterFix, type Fix } from "@core/location";
import { outboxErrorText } from "@core/messages";
import type { Sos, SosPhoto } from "../field/api";
import type { SosItem } from "../field/polling";
import { smsHref, sosSmsText } from "../field/sms";
import { currentSlot, slotLabel } from "../field/time";
import { categoryLabel, formatTime, type Strings } from "../i18n";
import { warRoomSms } from "../platform/config";
import { compressPhoto, isIOS, watchGps } from "../platform/browser";
import { useApp, useQuery, useSession, useSyncStatus } from "../ui/context";
import { Banner, Button, Card, Field, NumberField } from "../ui/components";

const CAT_ICON: Record<SosCategory, string> = { fake_voting: "🗳", evm_fault: "⚙", violence: "⚠", other: "❗" };
const GPS_MAX_AGE_MS = 2 * 60_000;
const MY_SOS_REFRESH_MS = 30_000;

export function PollingScreen() {
  const { app, t } = useApp();
  const { assemblyId } = useSession();
  const [code, setCode] = useState("");

  useEffect(() => {
    void app.lastBooth().then((c) => c && setCode((cur) => cur || c));
  }, [app]);

  const booths = useQuery(async () => (assemblyId ? app.core.store.listBooths(assemblyId) : []), [assemblyId], ["booths"]) ?? [];
  const booth = useMemo(() => (code.trim() ? findBoothByCode(booths, code) : null), [booths, code]);
  const boothProblem = !code.trim() ? null : !booth ? t.boothUnknown(normalizeDigits(code.trim()).toUpperCase()) : !app.canEdit(booth) ? t.boothNotAssigned(booth.code) : null;

  const worker = app.isWorker;
  return (
    <div>
      {worker ? (
        <Card testId="worker-booths">
          <p style={{ marginTop: 0, fontWeight: 700 }}>{t.workerHello(app.user?.name ?? "")}</p>
          {booths.length <= 12 ? <div className="label">{t.workerMyBooths}</div> : <div className="muted">{t.workerAnyBooth}</div>}
          {booths.length <= 12 ? <div className="chips">
            {booths.map((b) => (
              <button
                key={b.code}
                type="button"
                className={`chip${booth?.code === b.code ? " chip-on" : ""}`}
                onClick={() => {
                  setCode(b.code);
                  void app.rememberBooth(b.code);
                }}
                data-testid={`worker-booth-${b.code}`}
              >
                {t.boothNo} {b.code}
              </button>
            ))}
          </div> : null}
        </Card>
      ) : null}
      <Field
        label={t.sosBooth}
        name="polling-booth"
        value={code}
        onChange={setCode}
        placeholder={t.boothNoHint}
        error={boothProblem}
        hint={booth && !boothProblem ? `${booth.name} • ${booth.panchayat ?? ""}` : undefined}
      />
      <SosCard boothCode={booth && !boothProblem ? booth.code : null} />
      <MySosCard />
      {/* the war room's link is for anyone (a known voter, not only party workers): SOS only.
          Turnout is reported by logged-in surveyors / admins. */}
      {worker ? null : <TurnoutCard boothCode={booth && !boothProblem ? booth.code : null} />}
    </div>
  );
}

/* ====================================================================== SOS */

function SosCard({ boothCode }: { boothCode: string | null }) {
  const { app, t, lang } = useApp();
  const sync = useSyncStatus();
  const [category, setCategory] = useState<SosCategory | "">("");
  const [note, setNote] = useState("");
  const [clientId, setClientId] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number>(0);
  const [slow, setSlow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [fix, setFix] = useState<Fix | null>(null);
  const fixRef = useRef<Fix | null>(null);
  const [photo, setPhoto] = useState<(SosPhoto & { bytes: number }) | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoErr(false);
    setPhotoBusy(true);
    try {
      setPhoto(await compressPhoto(file));
    } catch {
      setPhoto(null);
      setPhotoErr(true);
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // keep a GPS fix ready while this screen is open, so the SOS never waits for it
  useEffect(() => {
    const w = watchGps(
      (f) => {
        const prev = fixRef.current && Date.now() - fixRef.current.timestamp < GPS_MAX_AGE_MS ? fixRef.current : null;
        fixRef.current = betterFix(prev, f);
        setFix(fixRef.current);
      },
      () => undefined
    );
    return () => w.stop();
  }, []);

  const state = useQuery(async () => (clientId ? app.sosState(clientId) : null), [clientId], ["outbox"]);
  // the war room's view of this alert, once it is on the server
  const mine = useQuery(() => app.mySos(), [], ["kv"]);
  const serverId = state?.state === "sent" ? state.record.result.sos?.id : undefined;
  const live = serverId != null ? mine?.alerts.find((a) => a.id === serverId) : undefined;

  useEffect(() => {
    if (!clientId) return;
    setSlow(false);
    const h = setTimeout(() => setSlow(true), 8_000);
    return () => clearTimeout(h);
  }, [clientId]);

  const send = async () => {
    if (!boothCode || !category) return;
    setBusy(true);
    setRefused(null);
    try {
      const f = fixRef.current && Date.now() - fixRef.current.timestamp < GPS_MAX_AGE_MS ? fixRef.current : null;
      const r = await app.raiseSos({ boothCode, category, note, fix: f, photo: photo ? { type: photo.type, data: photo.data } : null });
      if (!r.ok) {
        setRefused(r.reason === "not_assigned" ? t.boothNotAssigned(boothCode) : t.boothUnknown(boothCode));
        return;
      }
      setClientId(r.clientId);
      setSentAt(Date.now());
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setClientId(null);
    setCategory("");
    setNote("");
    setRefused(null);
    setPhoto(null);
    setPhotoErr(false);
  };

  const pendingItem = state && state.state !== "sent" ? state.item : null;
  const smsNumber = warRoomSms();
  const showSms = !!smsNumber && !!pendingItem && (sync.online === false || pendingItem.attempts > 0 || slow);
  const smsLink =
    showSms && pendingItem
      ? smsHref(
          smsNumber!,
          sosSmsText({
            assemblyId: pendingItem.assemblyId,
            boothCode: pendingItem.boothCode,
            category: pendingItem.payload.category,
            note: pendingItem.payload.note,
            lat: pendingItem.payload.lat,
            lng: pendingItem.payload.lng,
            accuracyM: pendingItem.payload.accuracyM,
            raisedAt: Date.parse(pendingItem.payload.raisedAt) || sentAt,
            clientId: pendingItem.key,
            name: app.user?.name,
          }),
          isIOS()
        )
      : null;

  return (
    <Card className="sos-card" testId="sos-card">
      <h2>🚨 {t.sosTitle}</h2>
      {!clientId ? (
        <>
          <div className="label">{t.sosCategory}</div>
          <div className="cat-grid" role="radiogroup" aria-label={t.sosCategory}>
            {SOS_CATEGORIES.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={category === c} className="cat" onClick={() => setCategory(c)} data-testid={`sos-cat-${c}`}>
                <span className="ico" aria-hidden>
                  {CAT_ICON[c]}
                </span>
                {categoryLabel(t, c)}
              </button>
            ))}
          </div>
          <div style={{ height: 12 }} />
          <Field label={t.sosNote} name="sos-note" value={note} onChange={setNote} maxLength={500} multiline />
          <div className="photo-row" data-testid="sos-photo">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void pickPhoto(e.target.files?.[0])} data-testid="sos-photo-input" />
            {photo ? (
              <>
                <img className="photo-thumb" src={`data:${photo.type};base64,${photo.data}`} alt="" />
                <span className="muted">{t.photoAttached(Math.max(1, Math.round(photo.bytes / 1024)))}</span>
                <Button variant="ghost" onClick={() => setPhoto(null)}>
                  {t.photoRemove}
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => fileRef.current?.click()} busy={photoBusy} testId="sos-photo-add">
                {photoBusy ? t.photoBusy : t.photoAdd}
              </Button>
            )}
          </div>
          {photoErr ? <Banner kind="warn">{t.photoFailed}</Banner> : null}
          <p className="muted" data-testid="sos-gps">
            {fix && fix.accuracyM != null ? t.sosGps(Math.round(fix.accuracyM)) : t.sosGpsNone}
          </p>
          {refused ? <Banner kind="error">{refused}</Banner> : null}
          <Button variant="sos" onClick={send} busy={busy} disabled={!boothCode || !category} testId="sos-send" sub={!category ? t.sosChooseCategory : undefined}>
            🚨 {t.sosSend}
          </Button>
        </>
      ) : (
        <div data-testid="sos-status">
          <div className={`status-box ${state?.state ?? "pending"}`}>
            {!state || (state.state === "pending" && sync.running && state.item.attempts === 0) ? (
              t.sosSending
            ) : state.state === "sent" ? (
              <span data-testid="sos-sent">
                {state.record.result.duplicate ? t.sosSentDup : t.sosSent(String(state.record.result.sos?.id ?? "?"), formatTime(state.record.sentAt, lang))}
              </span>
            ) : state.state === "pending" ? (
              <span data-testid="sos-waiting">{t.sosWaiting}</span>
            ) : (
              <span data-testid="sos-failed">
                {t.sosFailed} {state.item.lastError ? outboxErrorText(state.item.lastError, lang) : ""}
              </span>
            )}
            <div className="muted" style={{ marginTop: 6 }}>
              {t.boothNo} {(pendingItem ?? (state?.state === "sent" ? state.record : null))?.boothCode} •{" "}
              {pendingItem ? categoryLabel(t, pendingItem.payload.category) : state?.state === "sent" ? categoryLabel(t, (state.record.payload as SosItem["payload"]).category) : ""} • {t.sosByIdLabel}{" "}
              {clientId.slice(0, 8)}
            </div>
            {live ? <SosProgress alert={live} t={t} /> : null}
          </div>
          {state?.state === "sent" && state.record.result.warning === "location_ignored" ? (
            <Banner kind="warn" testId="sos-location-ignored">
              {t.sosLocationIgnored(!!state.record.result.sos?.location?.fromBooth)}
            </Banner>
          ) : null}
          {state?.state === "failed" ? (
            <Button variant="secondary" onClick={() => void app.polling.retry(state.item.id).then(() => app.pollingSync.trigger("manual"))}>
              {t.retry}
            </Button>
          ) : null}
          {smsLink ? (
            <div style={{ marginBottom: 12 }}>
              <a className="btn btn-secondary btn-big" href={smsLink} data-testid="sos-sms">
                ✉ {t.smsButton}
              </a>
              <p className="sms-note">{t.smsExplain}</p>
            </div>
          ) : null}
          <Button variant="secondary" onClick={reset} testId="sos-new">
            {t.sosNew}
          </Button>
        </div>
      )}
    </Card>
  );
}

/** open → acknowledged → resolved, with who / when / note. */
function SosProgress({ alert, t }: { alert: Sos; t: Strings }) {
  const { lang } = useApp();
  return (
    <div className={`sos-progress sos-${alert.status}`} data-testid={`sos-progress-${alert.id}`}>
      <div className="sos-steps" aria-hidden>
        <span className="on">●</span>
        <span className={alert.status !== "open" ? "on" : ""}>●</span>
        <span className={alert.status === "resolved" ? "on" : ""}>●</span>
      </div>
      <b data-testid="sos-status-text">{t[`sosSt_${alert.status}` as const]}</b>
      {alert.acknowledgedAt && alert.acknowledgedBy ? <div>{t.sosAckBy(alert.acknowledgedBy, formatTime(alert.acknowledgedAt, lang))}</div> : null}
      {alert.resolvedAt && alert.resolvedBy ? <div>{t.sosResolvedBy(alert.resolvedBy, formatTime(alert.resolvedAt, lang))}</div> : null}
      {alert.resolutionNote ? <div>{t.sosResolutionNote(alert.resolutionNote)}</div> : null}
    </div>
  );
}

/* ====================================================================== my SOS */

function MySosCard() {
  const { app, t, lang } = useApp();
  const sync = useSyncStatus();
  const mine = useQuery(() => app.mySos(), [], ["kv"]);
  const queued = useQuery(async () => (await app.pollingItems()).filter((i): i is SosItem => i.kind === "sos"), [], ["outbox"]) ?? [];
  const [all, setAll] = useState(false);

  // fetch now, every 30 s while the screen is visible, and when the network comes back
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") void app.refreshMySos();
    };
    refresh();
    const h = setInterval(refresh, MY_SOS_REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    return () => {
      clearInterval(h);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [app]);

  const alerts = mine?.alerts ?? [];
  if (!alerts.length && !queued.length) {
    return (
      <Card testId="my-sos">
        <h3 style={{ marginTop: 0 }}>{t.mySosTitle}</h3>
        <p className="muted">{t.mySosEmpty}</p>
      </Card>
    );
  }
  return (
    <Card testId="my-sos">
      <h3 style={{ marginTop: 0 }}>{t.mySosTitle}</h3>
      {mine ? (
        <p className="muted">
          {sync.online === false ? `${t.mySosOffline} • ` : ""}
          {t.mySosAsOf(formatTime(mine.fetchedAt, lang))}
        </p>
      ) : null}
      <ul className="sos-list">
        {queued.map((q) => (
          <li key={`q${q.id}`} className="sos-row sos-waiting">
            <b>
              {t.boothNo} {q.boothCode} • {categoryLabel(t, q.payload.category)}
            </b>
            <div className="muted">{t.sosRaisedAt(formatTime(q.payload.raisedAt, lang))}</div>
            <div className="sos-st">⏳ {t.sosSt_waiting}</div>
          </li>
        ))}
        {(all ? alerts : alerts.slice(0, 5)).map((a) => (
          <li key={a.id} className={`sos-row sos-${a.status}`} data-testid={`my-sos-${a.id}`}>
            <b>
              #{a.id} • {t.boothNo} {a.boothCode} • {categoryLabel(t, a.category)}
            </b>
            <div className="muted">
              {t.sosRaisedAt(formatTime(a.raisedAt, lang))}
              {a.note ? ` • “${a.note}”` : ""}
            </div>
            <SosProgress alert={a} t={t} />
          </li>
        ))}
      </ul>
      {!all && alerts.length > 5 ? (
        <Button variant="secondary" onClick={() => setAll(true)}>
          {t.mySosShowAll(alerts.length)}
        </Button>
      ) : null}
    </Card>
  );
}

/* ====================================================================== turnout */

function TurnoutCard({ boothCode }: { boothCode: string | null }) {
  const { app, t, lang } = useApp();
  const [slot, setSlot] = useState<TurnoutSlot>(() => currentSlot(Date.now()));
  const [votes, setVotes] = useState("");
  const [ours, setOurs] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error" | "info" | "warn"; text: string } | null>(null);

  const info = useQuery(
    async () => (boothCode ? { total: await app.totalVoters(boothCode), today: await app.turnoutToday(boothCode) } : null),
    [boothCode],
    ["outbox", "sections"]
  );
  const total = info?.total ?? null;
  const n = votes.trim() === "" ? null : Number(normalizeDigits(votes.trim()));
  const valid = n != null && Number.isInteger(n) && n >= 0 && n <= 100000;
  const overTotal = valid && total != null && n! > total;
  const pct = valid && !overTotal ? turnoutPct(n, total) : null;
  const o = ours.trim() === "" ? null : Number(normalizeDigits(ours.trim()));
  const oursValid = o == null || (Number.isInteger(o) && o >= 0 && (!valid || o <= n!));
  const oursPct = valid && o != null && oursValid && n! > 0 ? (o / n!) * 100 : null;
  const done = info?.today[slot];

  const send = async () => {
    if (!boothCode || !valid || !oursValid) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await app.reportTurnout({ boothCode, slot, votesPolled: n!, ourVotes: o });
      if (!r.ok) {
        setMsg({
          kind: "error",
          text: r.reason === "bad_number" ? t.turnoutBadNumber : r.reason === "bad_our_votes" ? t.ourVotesBad : r.reason === "not_assigned" ? t.boothNotAssigned(boothCode) : t.err_generic,
        });
        return;
      }
      setVotes("");
      setOurs("");
      setMsg({ kind: "info", text: t.turnoutQueued });
      // do not hold the button while a slow network tries; the slot shows ⏳ / ✓
      void r.sending.then(async () => {
        const today = await app.turnoutToday(boothCode);
        if (today[slot]?.state === "sent") setMsg({ kind: "ok", text: t.turnoutSent });
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId="turnout-card">
      <h2>{t.turnoutTitle}</h2>
      <div className="label">{t.turnoutSlot}</div>
      <div className="slot-grid" role="radiogroup" aria-label={t.turnoutSlot}>
        {TURNOUT_SLOTS.map((s) => {
          const d = info?.today[s];
          return (
            <button key={s} type="button" role="radio" aria-checked={slot === s} className="slot" onClick={() => setSlot(s)} data-testid={`slot-${s}`}>
              {slotLabel(s, lang)}
              {d ? (
                <span className="done">
                  {d.state === "sent" ? (d.warning ? "⚠" : "✓") : d.state === "pending" ? "⏳" : "✗"} {d.votesPolled}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div style={{ height: 12 }} />
      <NumberField label={t.votesPolled} name="votes" value={votes} onChange={setVotes} error={votes && !valid ? t.turnoutBadNumber : null} />
      <NumberField label={t.ourVotesLabel} name="our-votes" value={ours} onChange={setOurs} hint={oursPct != null ? t.ourVotesPct(oursPct.toFixed(1)) : t.ourVotesHint} error={ours && !oursValid ? t.ourVotesBad : null} />
      <p className="muted" data-testid="turnout-info">
        {total != null ? t.turnoutTotal(total) : t.turnoutNoTotal}
        {pct != null ? ` • ${t.turnoutPct(pct.toFixed(1))}` : ""}
      </p>
      {overTotal ? (
        <Banner kind="warn" testId="turnout-over-total">
          {t.turnoutOverTotalLocal(total!)}
        </Banner>
      ) : null}
      {msg ? (
        <Banner kind={msg.kind} testId="turnout-msg">
          {msg.text}
        </Banner>
      ) : null}
      {done?.state === "sent" && done.warning ? (
        <Banner kind="warn" testId="turnout-warning">
          {slotLabel(slot, lang)}: {done.warning === "more_than_total_voters" ? t.turnoutWarnOverTotal : t.turnoutWarnNoTotal}
        </Banner>
      ) : null}
      {done?.state === "failed" && done.error ? <Banner kind="error">{outboxErrorText(done.error, lang)}</Banner> : null}
      <Button big onClick={send} busy={busy} disabled={!boothCode || !valid || !oursValid} testId="turnout-send" sub={slotLabel(slot, lang)}>
        {t.turnoutSend}
      </Button>
    </Card>
  );
}
